import { useState, useEffect } from 'react';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, CartesianGrid, Legend, LineChart, Line } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Download, BarChart3, PieChartIcon, TrendingUp, Activity, AlertTriangle } from 'lucide-react';
import { QuotaLimitCard, isQuotaError } from '../components/QuotaLimitCard';
import ConnectivityProfile from '../components/ConnectivityProfile';

const COLORS = { 'Digitally Active': '#2D6E4E', 'Partially Visible': '#8A5A14', 'Digitally Invisible': '#8A1A1A' };
const DISTRICT_COLORS = ['#FE4A2A', '#2D6E4E', '#1A4D8A', '#8A5A14', '#5C2D8A', '#8A1A1A', '#090909'];
const TIER_COLORS_NEW = {
  trading_invisible: '#8A1A1A', shell: '#5A5751', dissolved: '#B8B4AD',
  partially_visible: '#6B9E56', fully_visible: '#2D6E4E',
};

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState(null);
  const [byPostcode, setByPostcode] = useState([]);
  const [bySector, setBySector] = useState([]);
  const [bbVsDigital, setBbVsDigital] = useState([]);
  const [timeSeries, setTimeSeries] = useState([]);
  const [pubStats, setPubStats] = useState(null);
  const [quotaHit, setQuotaHit] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/analytics/overview'),
      api.get('/analytics/by-postcode'),
      api.get('/analytics/by-sector'),
      api.get('/analytics/broadband-vs-digital'),
      api.get('/analytics/time-series'),
      api.get('/public/stats'),
    ]).then(([ov, pc, sec, bb, ts, pub]) => {
      setOverview(ov.data);
      setByPostcode(pc.data);
      setBySector(sec.data);
      setPubStats(pub.data || null);
      setBbVsDigital(bb.data);
      setTimeSeries((ts.data || []).map(s => ({
        date: s.date?.slice(5) || '',
        total: s.total_businesses,
        active: s.digitally_active,
        partial: s.partially_visible,
        invisible: s.digitally_invisible,
        verified: s.verified_live,
      })));
    }).catch(e => { if (isQuotaError(e)) setQuotaHit(e.response.data.detail); else console.error(e); });
  }, []);

  const scopeLabel = overview?.scope && overview.scope !== 'tvca' ? overview.scope_name : 'Tees Valley';
  const kpis = overview ? [
    { label: `Est. Businesses in ${scopeLabel}`, value: (overview.estimated_total_stockton_businesses || 16820).toLocaleString(), color: 'text-info-blue' },
    { label: 'Tracked & Scored', value: (overview.tracked_and_scored || overview.total_businesses)?.toLocaleString(), color: 'text-ink' },
    { label: 'CH Registered', value: overview.registered_in_db?.toLocaleString(), color: 'text-ai-purple' },
    { label: 'Ultrafast Coverage', value: `${overview.ultrafast_coverage_pct || 95}%`, color: 'text-status-green' },
    { label: 'Digitally Invisible', value: `${overview.digitally_invisible_pct || 55}%`, color: 'text-status-red' },
    { label: 'Full-Fibre Take-up', value: `${overview.full_fibre_takeup_pct || 22}%`, color: 'text-info-blue' },
  ] : [];

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink">Analytics Dashboard</h1>
          <p className="font-body text-sm text-ink/60 font-medium mt-1">Digital Adoption and Infrastructure Intelligence</p>
          <p className="font-body text-sm text-ink/50 max-w-3xl leading-relaxed mt-2">
            Live summary of business digital performance across {scopeLabel}. Connectivity coverage, digital adoption, deprivation overlay, sector gaps, and postcode hotspots — updated weekly.
          </p>
        </div>
      </div>

      {quotaHit && <QuotaLimitCard detail={quotaHit} context="Analytics" />}
      {quotaHit ? null : (<>

      {/* KPI Row */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpis.map((kpi, i) => (
            <div key={i} className="solid-card p-4 text-center animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`kpi-${kpi.label.toLowerCase().replace(/[% ]/g, '-')}`}>
              <span className="font-body text-xs uppercase tracking-[0.1em] text-ink/50 block mb-1">{kpi.label}</span>
              <span className={`font-display text-2xl font-extrabold ${kpi.color}`}>{kpi.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Borough Connectivity Baseline — every premises has access */}
      <ConnectivityProfile className="animate-fade-in" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tier Distribution Pie — 5-segment */}
        {pubStats?.tier_distribution && (() => {
          const td = pubStats.tier_distribution;
          const pieData = [
            { name: 'Trading Invisible', value: td.trading_invisible, fill: TIER_COLORS_NEW.trading_invisible },
            { name: 'Shell / Non-Trading', value: td.shell_non_trading, fill: TIER_COLORS_NEW.shell },
            { name: 'Dissolved / Winding', value: td.dissolved_dormant, fill: TIER_COLORS_NEW.dissolved },
            { name: 'Partially Visible', value: td.partially_visible, fill: TIER_COLORS_NEW.partially_visible },
            { name: 'Fully Visible', value: td.fully_visible, fill: TIER_COLORS_NEW.fully_visible },
          ];
          return (
            <div className="solid-card p-5 animate-fade-in" data-testid="chart-tier-distribution">
              <h3 className="font-display text-lg font-extrabold text-ink mb-4 flex items-center gap-2">
                <PieChartIcon className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Digital Tier Distribution
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={95} innerRadius={48} paddingAngle={1.5} dataKey="value">
                    {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 2 }}
                    formatter={(v) => v.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-between items-start mt-2 mb-3 px-1">
                <div>
                  <div className="font-display text-xl font-extrabold text-[#8A1A1A]">{td.invisible_total?.toLocaleString()}</div>
                  <div className="font-body text-[9px] text-ink/40">Invisible ({td.invisible_pct}%)</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl font-extrabold text-[#2D6E4E]">{td.visible_total?.toLocaleString()}</div>
                  <div className="font-body text-[9px] text-ink/40">Visible ({td.visible_pct}%)</div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-0.5">
                {pieData.map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-0.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: t.fill }} />
                      <span className="font-body text-[10px] text-ink/60">{t.name}</span>
                    </div>
                    <span className="font-body text-[10px] font-medium text-ink">{t.value?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Postcode Comparison Bar */}
        <div className="solid-card p-5 animate-fade-in" data-testid="chart-postcode-comparison">
          <h3 className="font-display text-lg font-extrabold text-ink mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Adoption by Postcode District
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byPostcode} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="district" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} />
              <YAxis tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 12, borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} />
              <Bar dataKey="active" name="Active" fill="#2D6E4E" radius={[2, 2, 0, 0]} />
              <Bar dataKey="partial" name="Partial" fill="#8A5A14" radius={[2, 2, 0, 0]} />
              <Bar dataKey="invisible" name="Invisible" fill="#8A1A1A" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Sector Heatmap */}
        <div className="solid-card p-5 animate-fade-in" data-testid="chart-sector-adoption">
          <h3 className="font-display text-lg font-extrabold text-ink mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Sector Adoption Rates
          </h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={bySector} layout="vertical" barSize={14}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <YAxis dataKey="sector" type="category" width={120} tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 12, borderRadius: 4 }} />
              <Bar dataKey="avg_score" name="Avg Score" fill="#FE4A2A" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Broadband vs Digital Score */}
        <div className="solid-card p-5 animate-fade-in" data-testid="chart-broadband-vs-digital">
          <h3 className="font-display text-lg font-extrabold text-ink mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Broadband Quality vs Digital Adoption
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="avg_download_speed" name="Avg Download (Mbps)" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} label={{ value: 'Avg Download Speed (Mbps)', position: 'bottom', fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <YAxis dataKey="avg_digital_score" name="Avg Digital Score" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} label={{ value: 'Avg Score', angle: -90, position: 'insideLeft', fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 12, borderRadius: 4 }} formatter={(val) => val} labelFormatter={() => ''} />
              <Scatter data={bbVsDigital} fill="#1A4D8A">
                {bbVsDigital.map((entry, i) => <Cell key={i} fill={DISTRICT_COLORS[i % DISTRICT_COLORS.length]} />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 3-Category Business Classification */}
      {pubStats?.data_quality && (() => {
        const cs = pubStats.data_quality;
        const td = pubStats.tier_distribution || {};
        const total = pubStats.total_businesses || 11849;
        return (
        <div className="solid-card p-5 animate-fade-in" data-testid="chart-business-categories">
          <h3 className="font-display text-lg font-extrabold text-ink mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Business Classification (Verified)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[
              { label: 'Trading Invisible', value: cs.trading_invisible, color: '#8A1A1A', pct: `${Math.round(cs.trading_invisible / total * 100)}%` },
              { label: 'Shell / Non-Trading', value: cs.shell_non_trading, color: '#5A5751', pct: `${Math.round(cs.shell_non_trading / total * 100)}%` },
              { label: 'Dissolved / Winding', value: cs.dissolved_dormant, color: '#B8B4AD', pct: `${Math.round(cs.dissolved_dormant / total * 100)}%` },
              { label: 'Partially Visible', value: td.partially_visible || 2171, color: '#6B9E56', pct: `${Math.round((td.partially_visible || 2171) / total * 100)}%` },
              { label: 'Fully Visible', value: td.fully_visible || 3161, color: '#2D6E4E', pct: `${Math.round((td.fully_visible || 3161) / total * 100)}%` },
            ].map((c, i) => (
              <div key={i} className="p-3 rounded border-l-[3px]" style={{ borderLeftColor: c.color, background: `${c.color}08` }}>
                <div className="font-display text-xl font-extrabold" style={{ color: c.color }}>{c.value?.toLocaleString()}</div>
                <div className="font-body text-[10px] text-ink/50">{c.label} ({c.pct})</div>
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={32}>
            <BarChart data={[{
              fully: td.fully_visible || 3161,
              partial: td.partially_visible || 2171,
              invisible: cs.trading_invisible,
              shell: cs.shell_non_trading,
              dissolved: cs.dissolved_dormant,
            }]} layout="horizontal" barSize={28}>
              <XAxis type="category" hide />
              <YAxis type="number" hide />
              <Bar dataKey="fully" stackId="a" fill="#2D6E4E" />
              <Bar dataKey="partial" stackId="a" fill="#6B9E56" />
              <Bar dataKey="invisible" stackId="a" fill="#8A1A1A" />
              <Bar dataKey="shell" stackId="a" fill="#5A5751" />
              <Bar dataKey="dissolved" stackId="a" fill="#B8B4AD" />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-between mt-2">
            <p className="font-body text-[10px] text-ink/30">
              {cs.three_categories_combined?.toLocaleString()} invisible ({cs.pct_of_register}%) &middot; {cs.trading_invisible?.toLocaleString()} are the true adoption gap
            </p>
            <p className="font-body text-[10px] text-ink/30">
              {(td.visible_total || cs.active_visible)?.toLocaleString()} visible ({td.visible_pct || 45}%)
            </p>
          </div>
        </div>
        );
      })()}

      {/* Time-series tracking */}
      {timeSeries.length > 0 && (
        <div className="solid-card p-5 animate-fade-in" data-testid="chart-time-series">
          <h3 className="font-display text-lg font-extrabold text-ink mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Digital Adoption Over Time
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={timeSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} />
              <YAxis tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 12, borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11 }} />
              <Line type="monotone" dataKey="total" name="Total Businesses" stroke="#090909" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="active" name="Digitally Active" stroke="#2D6E4E" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="partial" name="Partially Visible" stroke="#8A5A14" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="invisible" name="Digitally Invisible" stroke="#8A1A1A" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="verified" name="Verified Live" stroke="#5C2D8A" strokeWidth={2} dot={{ r: 4 }} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <p className="font-body text-xs text-ink/30 text-center">
        All charts reflect current register data. Sources: Companies House (OGL v3.0) · ONS IDBR · Ofcom Connected Nations.
        <a href="/privacy" className="underline ml-1">Privacy Policy</a>
      </p>
      </>)}
    </div>
  );
}
